import { Card, CardBody, CardHeader, CardHeading, SlateViewer, isEmptySlateValue } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DetailsPageInfoField } from "../../../../shared/ui/DetailsPageInfoField";
import { EventDetailResource } from "../../shared/types";
import { EventApplications } from "./EventApplications";

const InformationGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	grid-gap: ${({ theme }) => theme.spacing(2)};
`;

const StyledCardBody = styled(CardBody)`
	display: flex;
	flex-direction: column;
	grid-gap: ${({ theme }) => theme.spacing(5)};
`;

const Divider = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grey[30]};
`;

interface Props {
	event: EventDetailResource;
	isArchived?: boolean;
}

export const EventDetailsCard: FC<Props> = ({ event, isArchived = false }) => {
	const { t } = useTranslation();
	const shouldRenderDetailsCard =
		event.eventType.formatRequirement !== "prohibited" ||
		event.eventType.eventCategoryRequirement !== "prohibited" ||
		event.eventType.industryRequirement !== "prohibited" ||
		event.eventType.targetGroupRequirement !== "prohibited" ||
		event.eventType.regionRequirement !== "prohibited" ||
		event.eventType.primaryOrganizationalUnitRequirement !== "prohibited" ||
		event.eventType.organizationalUnitRequirement !== "prohibited" ||
		event.eventType.participationPermissionRequirement !== "prohibited" ||
		event.eventType.linkToEventRequirement !== "prohibited" ||
		event.eventType.tagRequirement !== "prohibited" ||
		event.eventType.preferenceRequirement !== "prohibited" ||
		event.eventType.isHighlightRequirement !== "prohibited" ||
		event.eventType.additionalInfoRequirement !== "prohibited" ||
		event.eventType.eventGoalRequirement !== "prohibited";

	return (
		<>
			{shouldRenderDetailsCard && (
				<Card>
					<CardHeader>
						<CardHeading>{t("Details")}</CardHeading>
					</CardHeader>
					<StyledCardBody>
						<InformationGrid>
							{event.eventType.eventGoalRequirement !== "prohibited" && (
								<DetailsPageInfoField title={t("Event goal")} value={event.eventGoal || "-"} />
							)}
							{event.eventType.formatRequirement !== "prohibited" && (
								<DetailsPageInfoField title={t("Format")} value={event.format ? t(event.format) : "-"} />
							)}
							{event.eventType.eventCategoryRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Category")}
									value={event.eventCategory ? event.eventCategory.name : "-"}
								/>
							)}
							{event.eventType.industryRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Industry", { count: event.industries.length })}
									value={event.industries.length > 0 ? event.industries.map((i) => i.name).join(", ") : "-"}
								/>
							)}
							{event.eventType.targetGroupRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Target group")}
									value={event.targetGroup ? event.targetGroup.name : "-"}
								/>
							)}
							{event.eventType.regionRequirement !== "prohibited" && (
								<DetailsPageInfoField title={t("Region")} value={event.region ? event.region.name : "-"} />
							)}
							{event.eventType.primaryOrganizationalUnitRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Primary organizational unit")}
									value={event.primaryOrganizationalUnit ? event.primaryOrganizationalUnit.name : "-"}
								/>
							)}
							{event.eventType.organizationalUnitRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Organizational unit", { count: event.organizationalUnits.length })}
									value={
										event.organizationalUnits.length > 0 ? event.organizationalUnits.map((o) => o.name).join(", ") : "-"
									}
								/>
							)}
							{event.eventType.participationPermissionRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Participation permission")}
									value={
										event.isInviteOnly !== null ? (event.isInviteOnly ? t("Only with invitation") : t("All")) : "-"
									}
								/>
							)}
							{event.eventType.linkToEventRequirement !== "prohibited" && (
								<DetailsPageInfoField title={t("Event link")} value={event.link ? event.link : "-"} />
							)}
							{event.eventType.tagRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Tag", { count: event.tags.length })}
									value={event.tags.length ? event.tags.map((tag) => tag.name).join(", ") : "-"}
								/>
							)}
							{event.eventType.preferenceRequirement !== "prohibited" && (
								<DetailsPageInfoField
									title={t("Preferences")}
									value={
										event.preferences.length ? event.preferences.map((preference) => preference.name).join(", ") : "-"
									}
								/>
							)}

							{event.eventType.isHighlightRequirement !== "prohibited" && (
								<DetailsPageInfoField title={t("Highlight")} value={event.isHighlight ? t("Yes") : t("No")} />
							)}
						</InformationGrid>
						{event.eventType.additionalInfoRequirement !== "prohibited" && event.additionalInfo && (
							<>
								<Divider />
								<DetailsPageInfoField
									title={t("Additional information")}
									value={isEmptySlateValue(event.additionalInfo) ? "-" : <SlateViewer content={event.additionalInfo} />}
								/>
							</>
						)}
					</StyledCardBody>
				</Card>
			)}
			{!isArchived && <EventApplications event={event} />}
		</>
	);
};
